import { createContext, useEffect, useState } from "react";
import Loading from "../Loading";
import * as Sentry from "@sentry/react";

export type ConfigValues = {
  httpURL: string;
  wsURL: string;
  appBaseUrl: string;
  heapAnalytics: string;
  cookieDomain?: string | null;
  statusBarHeight: number;
  environment: string;
  recipeLinkEnabled: boolean;
  blogEnabled: boolean;
  blogHomeFeedEnabled: boolean;
  reviewCountEnabled: boolean;
  version: string;
}

const configDefaultValue = {
  state: undefined as ConfigValues | undefined,
  setState: (_state: ConfigValues | undefined) => { }
};

export const ConfigContext = createContext(configDefaultValue);

export function mapConfigResponse(json: any): ConfigValues {
  return {
    httpURL: json.apiPubUrl,
    wsURL: json.wsApiUrl,
    appBaseUrl: json.appBaseUrl,
    heapAnalytics: json.heapAnalytics,
    cookieDomain: json.cookieDomain ?? null,
    statusBarHeight: Math.ceil(json.statusBarHeight ?? 0),
    environment: json.environment,
    recipeLinkEnabled: json.recipeLinkEnabled,
    blogEnabled: json.blogEnabled ?? false,
    blogHomeFeedEnabled: json.blogHomeFeedEnabled ?? false,
    reviewCountEnabled: json.reviewCountEnabled ?? false,
    version: json.version
  };
}

async function fetchClientConfig(): Promise<ConfigValues> {
  const response = await fetch("/auth/conf", {
    headers: { "accept": "application/json" },
  });
  if (!response.ok) {
    throw new Error("Unable to load configuration");
  }
  const json = await response.json();
  return mapConfigResponse(json);
}

export default function Config({ children, initialConfig, renderBeforeLoad = false }: Readonly<{
  children: React.ReactNode;
  initialConfig?: ConfigValues;
  /** Render children (with no config yet) instead of the loading screen. For error pages rendered without the root loader. */
  renderBeforeLoad?: boolean;
}>) {
  const [config, setConfig] = useState<ConfigValues | undefined>(initialConfig);

  useEffect(() => {
    if (!config) {
      (async () => {
        try {
          const conf = await fetchClientConfig();
          setConfig(conf);
        } catch (error) {
          console.error("Failed to load config", error);
        }
      })();
    }
  }, [config]);

  useEffect(() => {
    if (config) {
      Sentry.init({
        dsn: "https://581fe68b7a6528a53648b1f92867d85b@o4509022786879488.ingest.de.sentry.io/4509450461446224",
        environment: config.environment,
        // Sentry v11 collects user info, cookies, headers, bodies and GraphQL variables by
        // default. Keep the restrictive v10 behaviour; users are identified explicitly via setUser.
        dataCollection: {
          userInfo: false,
          cookies: false,
          httpHeaders: false,
          httpBodies: [],
          urlQueryParams: false,
          graphQL: { document: false, variables: false },
          genAI: { inputs: false, outputs: false },
          databaseQueryData: false,
          queues: false,
        },
      });
    }
  }, [config]);

  return (
    <>
      {config || renderBeforeLoad ?
        <ConfigContext.Provider value={{ state: config, setState: setConfig }}>
          {children}
        </ConfigContext.Provider>
        : <Loading />
      }
    </>
  )
}
